import { useEffect, useState } from 'react';
import { Globe, AlertTriangle, RefreshCw, Home } from 'lucide-react';
import Card from '@/components/ui/Card';
import api from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useUIStore } from '@/stores/ui-store';
import { cn } from '@/lib/utils';

interface TenantStats {
  active_tasks: number;
  completed_24h: number;
  failed_24h: number;
  escalated_24h: number;
  success_rate_24h: number | null;
  pipeline_orders_24h: number;
  pipeline_success_rate_24h: number | null;
  unread_notifications: number;
  open_escalations: number;
  prospects_active: number;
  suppliers_active: number;
  avg_fulfillment_hours_7d: number | null;
}

interface WorstTool {
  tool_name: string;
  invocations: number;
  success_rate: number | null;
  p95_ms: number | null;
}

interface TenantRow {
  site_id: string;
  site_name: string;
  is_self: boolean;
  error: string | null;
  stats: TenantStats | null;
  worst_tools: WorstTool[];
  tools_error: string | null;
}

interface FleetResponse {
  tenants: TenantRow[];
}

function fmtRate(v: number | null | undefined, digits = 1): string {
  if (v == null) return '—';
  return `${Number(v).toFixed(digits)}%`;
}

function fmtNum(v: number | null | undefined): string {
  if (v == null) return '—';
  return Number(v).toLocaleString();
}

function fmtHours(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${Number(v).toFixed(1)}h`;
}

function rateColor(v: number | null | undefined): string {
  if (v == null) return 'text-stone-500';
  if (v >= 90) return 'text-green-700';
  if (v >= 75) return 'text-amber-700';
  return 'text-red-700';
}

export default function AgentFleetDashboard() {
  const user = useAuthStore((s) => s.user);
  const addToast = useUIStore((s) => s.addToast);
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setIsLoading(true);
      else setRefreshing(true);
      const res = await api.get<FleetResponse>('/admin/platform/agent/fleet');
      setTenants(res.tenants);
    } catch (err) {
      if (!silent) {
        addToast(err instanceof Error ? err.message : 'Failed to load fleet dashboard', 'error');
      }
    } finally {
      setIsLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!user?.isPlatformAdmin) return;
    fetchData();
    const interval = setInterval(() => fetchData(true), 30_000);
    return () => clearInterval(interval);
  }, [user?.isPlatformAdmin]);

  if (!user?.isPlatformAdmin) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <Card className="p-6 border-red-200 bg-red-50">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <p className="text-red-900 font-medium">Platform admin access required.</p>
          </div>
        </Card>
      </div>
    );
  }

  const tenantCount = tenants?.length ?? 0;
  const reachable = tenants?.filter((t) => !t.error).length ?? 0;

  // Fleet-wide aggregates for the top summary strip.
  const totals = (tenants ?? []).reduce(
    (acc, t) => {
      if (!t.stats) return acc;
      acc.active_tasks += t.stats.active_tasks;
      acc.completed_24h += t.stats.completed_24h;
      acc.failed_24h += t.stats.failed_24h;
      acc.escalated_24h += t.stats.escalated_24h;
      acc.pipeline_orders_24h += t.stats.pipeline_orders_24h;
      acc.open_escalations += t.stats.open_escalations;
      acc.unread_notifications += t.stats.unread_notifications;
      acc.prospects_active += t.stats.prospects_active;
      return acc;
    },
    {
      active_tasks: 0,
      completed_24h: 0,
      failed_24h: 0,
      escalated_24h: 0,
      pipeline_orders_24h: 0,
      open_escalations: 0,
      unread_notifications: 0,
      prospects_active: 0,
    },
  );
  const fleetSuccessRate =
    totals.completed_24h + totals.failed_24h + totals.escalated_24h > 0
      ? (totals.completed_24h * 100.0) /
        (totals.completed_24h + totals.failed_24h + totals.escalated_24h)
      : null;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <Globe className="w-7 h-7 text-sage-700" />
            <h1 className="text-2xl font-bold text-stone-900">Dippy Fleet</h1>
          </div>
          <p className="text-stone-500">
            Side-by-side view of every tenant running Dippy. Stats refresh every 30s.
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing || isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-stone-300 bg-white text-stone-700 hover:bg-stone-50 disabled:opacity-50"
        >
          <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
          Refresh
        </button>
      </div>

      {isLoading && (
        <Card className="p-6">
          <p className="text-sm text-stone-500">Loading fleet data…</p>
        </Card>
      )}

      {!isLoading && tenants && tenantCount === 0 && (
        <Card className="p-6">
          <p className="text-sm text-stone-700">
            No tenants configured. Set the <code>FLEET_PEERS</code> env var on this
            backend to include other tenant DB URLs, then restart.
          </p>
        </Card>
      )}

      {!isLoading && tenants && tenantCount > 0 && (
        <>
          {/* Fleet summary strip */}
          <Card className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <Summary label="Tenants" value={`${reachable}/${tenantCount}`} />
              <Summary
                label="Active tasks"
                value={fmtNum(totals.active_tasks)}
              />
              <Summary
                label="Success 24h"
                value={fmtRate(fleetSuccessRate)}
                valueClass={rateColor(fleetSuccessRate)}
              />
              <Summary label="Escalated 24h" value={fmtNum(totals.escalated_24h)} />
              <Summary
                label="Pipeline 24h"
                value={fmtNum(totals.pipeline_orders_24h)}
              />
              <Summary
                label="Open escalations"
                value={fmtNum(totals.open_escalations)}
                valueClass={totals.open_escalations > 0 ? 'text-red-700' : ''}
              />
            </div>
          </Card>

          {/* Per-tenant cards */}
          <div className="grid gap-4 lg:grid-cols-2 2xl:grid-cols-3">
            {tenants.map((t) => (
              <TenantCard key={t.site_id} tenant={t} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Summary({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-stone-500">{label}</div>
      <div className={cn('text-2xl font-bold text-stone-900 mt-0.5', valueClass)}>
        {value}
      </div>
    </div>
  );
}

function TenantCard({ tenant }: { tenant: TenantRow }) {
  const s = tenant.stats;
  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {tenant.is_self && <Home className="w-4 h-4 text-stone-400" />}
          <h2 className="font-semibold text-stone-900">{tenant.site_name}</h2>
          <span className="text-xs text-stone-500">{tenant.site_id}</span>
        </div>
        {tenant.error ? (
          <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded">
            unreachable
          </span>
        ) : s && s.escalated_24h > 0 ? (
          <span className="text-xs font-medium text-red-700 bg-red-50 px-2 py-0.5 rounded">
            {s.escalated_24h} escalated
          </span>
        ) : s && s.open_escalations > 0 ? (
          <span className="text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
            {s.open_escalations} open
          </span>
        ) : (
          <span className="text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded">
            healthy
          </span>
        )}
      </div>

      {tenant.error ? (
        <p className="text-xs text-stone-500 font-mono truncate">{tenant.error}</p>
      ) : s ? (
        <>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <Metric label="Active" value={fmtNum(s.active_tasks)} />
            <Metric
              label="Success 24h"
              value={fmtRate(s.success_rate_24h)}
              valueClass={rateColor(s.success_rate_24h)}
            />
            <Metric label="Failed 24h" value={fmtNum(s.failed_24h)} />
            <Metric label="Pipeline 24h" value={fmtNum(s.pipeline_orders_24h)} />
            <Metric
              label="Pipeline OK"
              value={fmtRate(s.pipeline_success_rate_24h)}
              valueClass={rateColor(s.pipeline_success_rate_24h)}
            />
            <Metric label="Avg fulfill" value={fmtHours(s.avg_fulfillment_hours_7d)} />
            <Metric label="Prospects" value={fmtNum(s.prospects_active)} />
            <Metric label="Suppliers" value={fmtNum(s.suppliers_active)} />
            <Metric
              label="Unread"
              value={fmtNum(s.unread_notifications)}
              valueClass={s.unread_notifications > 0 ? 'text-amber-700' : ''}
            />
          </div>

          {tenant.worst_tools.length > 0 && (
            <div className="pt-2 border-t border-stone-100">
              <div className="text-xs uppercase tracking-wide text-stone-500 mb-1.5">
                Worst tools (7d)
              </div>
              <div className="space-y-1">
                {tenant.worst_tools.map((t) => (
                  <div
                    key={t.tool_name}
                    className="flex items-center justify-between text-xs"
                  >
                    <span className="font-mono text-stone-700 truncate">
                      {t.tool_name}
                    </span>
                    <span className="flex items-center gap-3 text-stone-500 shrink-0">
                      <span>{fmtNum(t.invocations)} runs</span>
                      <span className={rateColor(t.success_rate)}>
                        {fmtRate(t.success_rate)}
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-stone-500">No data.</p>
      )}
    </Card>
  );
}

function Metric({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div>
      <div className="text-xs text-stone-500">{label}</div>
      <div className={cn('font-semibold text-stone-900', valueClass)}>{value}</div>
    </div>
  );
}
